/* ----------------------------------------------------------------------------
   SupplyTable — React port of PS.dataTable. Renders the mockup `.card`/`.tbl`
   markup (so the scoped CSS applies 1:1) with search, 3-state sort, pagination +
   page-size, .xlsx export, row kebab actions, row click, bulk actions, expandable
   rows, and a Filters drawer (multi / text / range / date). The backbone table
   for nearly every Supply-Side screen.

   columns: [{key, label, render?(r), value?(r), align?, sortable?, export?}]
   filters: [{key, label, type:'multi'|'text'|'range'|'date', options?, predicate?, unit?}]
   rowActions(r): [{label, icon, onClick, danger, hidden, sep}]
   bulkActions:   [{label, icon, onClick(rows, clear), danger, confirm}]
   ---------------------------------------------------------------------------- */
import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Menu, MenuItem, ListItemIcon, Divider} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import {cx, exportTable} from './helpers'
import SideDrawer from './SideDrawer'
import ConfirmDialog from './ConfirmDialog'

const getVal = (r, col) => (col.value ? col.value(r) : r[col.key])

export default function SupplyTable({
  columns,
  rows: rowsProp,
  rowKey = (r) => r.id,
  searchKeys = [],
  search,
  searchPlaceholder = 'Search...',
  hideSearch = false,
  pageSize = 20,
  defaultSort,
  filters,
  rowActions,
  rowClick,
  bulkActions,
  expand,
  initialExpandedKey,
  exportName = 'export',
  toolbarRight,
  emptyText = 'No data.',
  emptyCta,
  /* Optional server-driven mode. When provided, the table renders `rows` AS the
     current page (no client filter/sort/slice) and delegates paging/sort/search
     to the parent: {total, page, onPage, size, onSize, sortKey, sortDir, onSort,
     onSearch, loading}. Omitting it keeps the original 100% client-side behavior. */
  server,
}) {
  const serverMode = !!server
  const rowsAll = typeof rowsProp === 'function' ? rowsProp() : rowsProp || []
  /* server mode may pass `query` so a remounted table starts with the term already applied upstream */
  const [q, setQ] = useState(() => (serverMode && server.query != null ? String(server.query) : ''))
  const [sort, setSort] = useState(defaultSort ? defaultSort.key : columns[0] && columns[0].key)
  const [dir, setDir] = useState(defaultSort ? defaultSort.dir : 'asc')
  const [page, setPage] = useState(1)
  const [sizeState, setSizeState] = useState(pageSize)
  const [sel, setSel] = useState(() => new Set())
  const [expanded, setExpanded] = useState(() =>
    initialExpandedKey != null ? new Set([String(initialExpandedKey)]) : new Set()
  )
  // open (and keep open) a row requested by the parent — e.g. just-created/edited row
  useEffect(() => {
    if (initialExpandedKey == null) return
    setExpanded((prev) => {
      const k = String(initialExpandedKey)
      if (prev.has(k)) return prev
      const next = new Set(prev)
      next.add(k)
      return next
    })
  }, [initialExpandedKey])
  const [filterState, setFilterState] = useState({})
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [kebab, setKebab] = useState({anchor: null, row: null})
  const [bulkConfirm, setBulkConfirm] = useState(null)

  const hasExpand = typeof expand === 'function'
  const selectable = !!(bulkActions && bulkActions.length)

  const filtered = useMemo(() => {
    let rows = rowsAll.slice()
    if (q) {
      const qq = q.toLowerCase()
      rows = rows.filter((r) =>
        search
          ? search(r, qq)
          : searchKeys.some((k) => String(r[k] == null ? '' : r[k]).toLowerCase().indexOf(qq) >= 0)
      )
    }
    ;(filters || []).forEach((f) => {
      const selF = filterState[f.key]
      if (!selF) return
      const type = f.type || 'multi'
      const fval = (r) => (f.value ? f.value(r) : r[f.field || f.key])
      if (type === 'multi' && selF instanceof Set && selF.size) {
        rows = rows.filter((r) => (f.predicate ? f.predicate(r, selF) : selF.has(String(fval(r)))))
      } else if (type === 'text' && selF.q) {
        const tq = selF.q.toLowerCase()
        rows = rows.filter((r) =>
          f.predicate ? f.predicate(r, selF) : String(fval(r) == null ? '' : fval(r)).toLowerCase().indexOf(tq) >= 0
        )
      } else if (type === 'range') {
        const mn = selF.min != null && selF.min !== '' ? +selF.min : null
        const mx = selF.max != null && selF.max !== '' ? +selF.max : null
        if (mn != null || mx != null)
          rows = rows.filter((r) => {
            const v = +fval(r)
            return !isNaN(v) && (mn == null || v >= mn) && (mx == null || v <= mx)
          })
      }
    })
    const col = columns.find((c) => c.key === sort)
    if (col) {
      const s = dir === 'asc' ? 1 : -1
      rows.sort((a, b) => {
        let x = getVal(a, col)
        let y = getVal(b, col)
        if (typeof x === 'string') {
          x = x.toLowerCase()
          y = String(y == null ? '' : y).toLowerCase()
        }
        return x < y ? -s : x > y ? s : 0
      })
    }
    return rows
  }, [rowsAll, q, filterState, filters, columns, sort, dir, search, searchKeys])

  const size = serverMode ? server.size : sizeState
  const total = serverMode ? server.total || 0 : filtered.length
  const pages = Math.max(1, Math.ceil(total / size))
  const curPage = serverMode ? server.page : Math.min(page, pages)
  const start = (curPage - 1) * size
  const pageRows = serverMode ? rowsAll : filtered.slice(start, start + size)
  const colCount = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (hasExpand ? 1 : 0)
  // active sort indicator + page setter resolve to server handlers when in server mode
  const activeSort = serverMode ? server.sortKey : sort
  const activeDir = serverMode ? server.sortDir : dir
  const goPage = (n) => (serverMode ? server.onPage(n) : setPage(n))
  const setSize = (n) => (serverMode ? server.onSize(n) : setSizeState(n))
  // count-agnostic server paging (like the app's ProzoDataGrid + skipCount): when the
  // backend doesn't return a reliable total, page by cursor — "next" stays enabled
  // until a short (partial) page comes back.
  const unknownTotal = serverMode && server.total == null
  // "next" disabled: prefer the parent's explicit hasNext flag (unambiguous); else
  // fall back to count math / cursor heuristic.
  const nextDisabled = serverMode
    ? server.hasNext != null
      ? !server.hasNext
      : unknownTotal
      ? pageRows.length < size
      : curPage >= pages
    : curPage >= pages

  // server-mode search: debounce keystrokes, then ask the parent to refetch.
  // Skip the initial mount so we don't reset the parent's page on first render.
  const searchMounted = useRef(false)
  useEffect(() => {
    if (!serverMode) return undefined
    if (!searchMounted.current) {
      searchMounted.current = true
      return undefined
    }
    const id = setTimeout(() => server.onSearch(q), 350)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, serverMode])

  const filterCount = useMemo(() => {
    let n = 0
    ;(filters || []).forEach((f) => {
      const s = filterState[f.key]
      if (!s) return
      if (s instanceof Set) n += s.size
      else if (s.q) n++
      else if ((s.min != null && s.min !== '') || (s.max != null && s.max !== '')) n++
    })
    return n
  }, [filters, filterState])

  const toggleSort = (key, sortable) => {
    if (sortable === false) return
    if (serverMode) {
      const nd = server.sortKey === key ? (server.sortDir === 'asc' ? 'desc' : 'asc') : 'asc'
      server.onSort(key, nd)
      return
    }
    if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSort(key)
      setDir('asc')
    }
  }

  const toggleSel = (k) => {
    setSel((prev) => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  }
  const clearSel = () => setSel(new Set())
  const pageAllSelected = pageRows.length > 0 && pageRows.every((r) => sel.has(String(rowKey(r))))
  const selectedRows = rowsAll.filter((r) => sel.has(String(rowKey(r))))

  const runBulk = (a) => {
    const go = () => a.onClick(selectedRows, clearSel)
    if (a.confirm === false) go()
    else
      setBulkConfirm({
        title: `${a.label} ${selectedRows.length} item${selectedRows.length !== 1 ? 's' : ''}?`,
        body: `This will apply "${a.label}" to the selected row${selectedRows.length !== 1 ? 's' : ''}.`,
        okLabel: a.label,
        danger: !!a.danger,
        onOk: go,
      })
  }

  const doExport = () => {
    const cols = columns
      .filter((c) => c.export !== false)
      .map((c) => ({label: c.label, value: (r) => (c.exportValue ? c.exportValue(r) : getVal(r, c))}))
    exportTable(filtered, cols, exportName)
  }

  const kebabActions = kebab.row && rowActions ? (rowActions(kebab.row) || []).filter((a) => a && !a.hidden) : []

  return (
    <div className='card'>
      <div className='card-pad' style={{paddingBottom: 0}}>
        <div className='toolbar'>
          {hideSearch ? null : (
            <div className='search'>
              <SearchIcon style={{width: 18, height: 18, color: 'var(--neutral)'}} />
              <input
                type='text'
                placeholder={searchPlaceholder}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  // server mode resets the page inside the debounced onSearch handler
                  if (!serverMode) setPage(1)
                }}
              />
            </div>
          )}
          <div className='spacer' />
          {toolbarRight}
          {filters && filters.length ? (
            <button className='btn' onClick={() => setDrawerOpen(true)}>
              <FilterListIcon style={{width: 16, height: 16}} />
              Filters{filterCount ? ` (${filterCount})` : ''}
            </button>
          ) : null}
          {exportName !== false ? (
            <button className='btn' onClick={doExport}>
              <DownloadOutlinedIcon style={{width: 16, height: 16}} />
              Export
            </button>
          ) : null}
        </div>
      </div>

      <div className='tbl-wrap'>
        <table className='tbl rowanim'>
          <thead>
            <tr>
              {selectable ? (
                <th style={{width: 34}}>
                  <label className='chk'>
                    <input
                      type='checkbox'
                      checked={pageAllSelected}
                      onChange={(e) => {
                        setSel((prev) => {
                          const next = new Set(prev)
                          pageRows.forEach((r) => {
                            const k = String(rowKey(r))
                            e.target.checked ? next.add(k) : next.delete(k)
                          })
                          return next
                        })
                      }}
                    />
                  </label>
                </th>
              ) : null}
              {hasExpand ? <th style={{width: 30}} /> : null}
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={c.sortable !== false ? 'sortable' : undefined}
                  style={c.align ? {textAlign: c.align} : undefined}
                  onClick={() => toggleSort(c.key, c.sortable)}
                >
                  {c.label}
                  {c.sortable !== false ? (
                    <span className='arrow'>{activeSort === c.key ? (activeDir === 'asc' ? '▲' : '▼') : ''}</span>
                  ) : null}
                </th>
              ))}
              {rowActions ? <th style={{width: 44}} /> : null}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={colCount}>
                  <div className='empty'>
                    <div className='ic'>
                      {q || filterCount ? (
                        <SearchIcon style={{width: 30, height: 30}} />
                      ) : (
                        <InboxOutlinedIcon style={{width: 30, height: 30}} />
                      )}
                    </div>
                    <h3>{q ? `No results for '${q}'` : filterCount ? 'No results' : 'Nothing here yet'}</h3>
                    <p>{q || filterCount ? 'Try adjusting your filters.' : emptyText}</p>
                    {!(q || filterCount) && emptyCta ? (
                      <button className='btn btn-sm' onClick={emptyCta.onClick}>
                        {emptyCta.icon}
                        {emptyCta.label}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ) : (
              pageRows.map((r, i) => {
                const k = String(rowKey(r))
                const isOpen = expanded.has(k)
                return (
                  <React.Fragment key={k}>
                    <tr
                      className={rowClick ? 'clickable' : undefined}
                      style={{animationDelay: `${i * 22}ms`}}
                      onClick={(e) => {
                        if (e.target.closest('input,button,a,label,.kebab')) return
                        if (rowClick) rowClick(r)
                      }}
                    >
                      {selectable ? (
                        <td>
                          <label className='chk'>
                            <input type='checkbox' checked={sel.has(k)} onChange={() => toggleSel(k)} />
                          </label>
                        </td>
                      ) : null}
                      {hasExpand ? (
                        <td>
                          <button
                            className='iconbtn'
                            onClick={() =>
                              setExpanded((prev) => {
                                const next = new Set(prev)
                                next.has(k) ? next.delete(k) : next.add(k)
                                return next
                              })
                            }
                          >
                            {isOpen ? '▾' : '▸'}
                          </button>
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td key={c.key} style={c.align ? {textAlign: c.align} : undefined}>
                          {c.render ? c.render(r) : getVal(r, c)}
                        </td>
                      ))}
                      {rowActions ? (
                        <td>
                          <div className='kebab'>
                            <button
                              className='kebab-btn'
                              onClick={(e) => setKebab({anchor: e.currentTarget, row: r})}
                            >
                              <MoreVertIcon style={{width: 18, height: 18}} />
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                    {hasExpand && isOpen ? (
                      <tr className='exp-row'>
                        <td colSpan={colCount}>
                          <div className='exp-content'>{expand(r)}</div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className='card-pad' style={{paddingTop: 0}}>
        <div className='pager'>
          <div className='info'>
            {unknownTotal
              ? `Page ${curPage}${pageRows.length < size ? ' · end' : ''}`
              : total
              ? `Showing ${start + 1}–${Math.min(start + size, total)} of ${total} results`
              : '0 results'}
          </div>
          <div className='ctrls'>
            <span className='info'>Rows</span>
            <select
              value={size}
              onChange={(e) => {
                setSize(+e.target.value)
                goPage(serverMode ? 1 : 1)
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
            <button className='pg-btn' disabled={curPage <= 1} onClick={() => goPage(curPage - 1)}>
              ‹
            </button>
            {unknownTotal ? (
              <button className='pg-btn active'>{curPage}</button>
            ) : (
              pageWindow(curPage, pages).map((p, idx) =>
                p === '…' ? (
                  <span key={'e' + idx} style={{color: 'var(--neutral)', padding: '0 2px'}}>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={cx('pg-btn', p === curPage && 'active')}
                    onClick={() => goPage(p)}
                  >
                    {p}
                  </button>
                )
              )
            )}
            <button className='pg-btn' disabled={nextDisabled} onClick={() => goPage(curPage + 1)}>
              ›
            </button>
          </div>
        </div>
      </div>

      {selectable && sel.size > 0 ? (
        <div className='bulkbar show'>
          <span className='sel'>{sel.size} selected</span>
          {bulkActions.map((a, i) => (
            <button className='btn btn-sm' key={i} onClick={() => runBulk(a)}>
              {a.icon}
              {a.label}
            </button>
          ))}
          <button className='btn btn-sm btn-ghost' style={{marginLeft: 'auto'}} onClick={clearSel}>
            Clear
          </button>
        </div>
      ) : null}

      <Menu
        anchorEl={kebab.anchor}
        open={!!kebab.anchor}
        onClose={() => setKebab({anchor: null, row: null})}
        PaperProps={{className: 'supply-scope ss-modal-paper'}}
      >
        {kebabActions.map((a, i) =>
          a.sep ? (
            <Divider key={'s' + i} />
          ) : (
            <MenuItem
              key={i}
              onClick={() => {
                setKebab({anchor: null, row: null})
                if (a.onClick) a.onClick(kebab.row)
              }}
              sx={a.danger ? {color: 'error.main'} : undefined}
            >
              {a.icon ? <ListItemIcon sx={a.danger ? {color: 'error.main'} : undefined}>{a.icon}</ListItemIcon> : null}
              {a.label}
            </MenuItem>
          )
        )}
      </Menu>

      {filters && filters.length ? (
        <SideDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title='Filters'
          footer={
            <>
              <button className='btn btn-ghost' onClick={() => setFilterState({})}>
                Clear all filters
              </button>
              <button className='btn btn-primary' onClick={() => setDrawerOpen(false)}>
                Apply
              </button>
            </>
          }
        >
          <FiltersBody filters={filters} state={filterState} setState={setFilterState} />
        </SideDrawer>
      ) : null}

      <ConfirmDialog
        open={!!bulkConfirm}
        onClose={() => setBulkConfirm(null)}
        title={bulkConfirm ? bulkConfirm.title : ''}
        body={bulkConfirm ? bulkConfirm.body : ''}
        okLabel={bulkConfirm ? bulkConfirm.okLabel : ''}
        danger={bulkConfirm ? bulkConfirm.danger : false}
        onOk={() => {
          if (bulkConfirm) bulkConfirm.onOk()
          setBulkConfirm(null)
        }}
      />
    </div>
  )
}

function pageWindow(page, pages) {
  const out = []
  let lo = Math.max(1, page - 2)
  const hi = Math.min(pages, lo + 4)
  lo = Math.max(1, hi - 4)
  if (lo > 1) {
    out.push(1)
    if (lo > 2) out.push('…')
  }
  for (let i = lo; i <= hi; i++) out.push(i)
  if (hi < pages) {
    if (hi < pages - 1) out.push('…')
    out.push(pages)
  }
  return out
}

function FiltersBody({filters, state, setState}) {
  const upd = (key, val) => setState((prev) => ({...prev, [key]: val}))
  return (
    <>
      {filters.map((f) => {
        const type = f.type || 'multi'
        if (type === 'multi') {
          const sel = state[f.key] instanceof Set ? state[f.key] : new Set()
          return (
            <div className='field' key={f.key}>
              <label>{f.label}</label>
              {(f.options || []).map((o) => (
                <label className='checkrow' key={o.value}>
                  <input
                    type='checkbox'
                    checked={sel.has(o.value)}
                    onChange={(e) => {
                      const next = new Set(sel)
                      e.target.checked ? next.add(o.value) : next.delete(o.value)
                      upd(f.key, next)
                    }}
                  />{' '}
                  {o.label}
                </label>
              ))}
            </div>
          )
        }
        if (type === 'text') {
          const v = (state[f.key] && state[f.key].q) || ''
          return (
            <div className='field' key={f.key}>
              <label>{f.label}</label>
              <input
                className='input'
                placeholder={f.placeholder || 'Contains…'}
                value={v}
                onChange={(e) => upd(f.key, {q: e.target.value.trim()})}
              />
            </div>
          )
        }
        if (type === 'range') {
          const rv = state[f.key] || {}
          return (
            <div className='field' key={f.key}>
              <label>
                {f.label}
                {f.unit ? <span className='muted'> ({f.unit})</span> : null}
              </label>
              <div className='filter-range'>
                <input
                  className='input'
                  type='number'
                  placeholder='Min'
                  value={rv.min != null ? rv.min : ''}
                  onChange={(e) => upd(f.key, {...rv, min: e.target.value === '' ? null : +e.target.value})}
                />
                <span className='muted'>–</span>
                <input
                  className='input'
                  type='number'
                  placeholder='Max'
                  value={rv.max != null ? rv.max : ''}
                  onChange={(e) => upd(f.key, {...rv, max: e.target.value === '' ? null : +e.target.value})}
                />
              </div>
            </div>
          )
        }
        return null
      })}
    </>
  )
}

SupplyTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  rowKey: PropTypes.func,
  searchKeys: PropTypes.array,
  search: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  hideSearch: PropTypes.bool,
  pageSize: PropTypes.number,
  defaultSort: PropTypes.object,
  filters: PropTypes.array,
  rowActions: PropTypes.func,
  rowClick: PropTypes.func,
  bulkActions: PropTypes.array,
  expand: PropTypes.func,
  initialExpandedKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exportName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  toolbarRight: PropTypes.node,
  emptyText: PropTypes.string,
  emptyCta: PropTypes.object,
  server: PropTypes.object,
}

// keep an icon import referenced for agents who want the empty-table glyph
export {TableChartOutlinedIcon}
